/**
 * Den Kopf einer Kreditkartenabrechnung korrigieren: Anbieter, Endziffern,
 * Inhaber, Zeitraum, die beiden Salden und die Hinweise auf faelligen
 * Betrag, Mindestbetrag und Einzug. Ein Saldo ist ein Betrag ohne
 * Vorzeichen plus die Angabe Schuld oder Guthaben, genau wie er auf der
 * Abrechnung steht. Der Server rechnet daraus den Saldo aus Sicht des
 * Karteninhabers und baut die Kennung der Karte neu.
 *
 * Geschickt wird nur, was sich geaendert hat: ein unveraenderter Anbieter
 * soll weder die Kennung noch die Bank neu setzen.
 */
import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type AuszugVoll, type Kartenabrechnung, type SaldoArt } from '../lib/api'
import { datum } from '../lib/format'
import { betragOhneZeichen, saldoArtAus } from '../lib/karte'
import { Fenster } from './Fenster'

type Werte = {
  anbieter: string
  endziffern: string
  inhaber: string
  zeitraum_von: string
  zeitraum_bis: string
  anfangssaldo: string
  schlusssaldo: string
  faelliger_betrag: string
  mindestbetrag: string
  ausgleich_betrag: string
}

function SaldoArtWahl({ wert, setzeWert, beschriftung }: { wert: SaldoArt | null; setzeWert: (w: SaldoArt) => void; beschriftung: string }) {
  return (
    <div className="wahl" role="group" aria-label={beschriftung}>
      <button type="button" aria-pressed={wert === 'schuld'} onClick={() => setzeWert('schuld')}>Schuld</button>
      <button type="button" aria-pressed={wert === 'guthaben'} onClick={() => setzeWert('guthaben')}>Guthaben</button>
    </div>
  )
}

export function KartenKopfBearbeiten({ auszug, abrechnung, offen, aufSchliessen }: {
  auszug: AuszugVoll
  abrechnung: Kartenabrechnung
  offen: boolean
  aufSchliessen: () => void
}) {
  const abfragen = useQueryClient()
  const [anfang] = useState<Werte>(() => ({
    anbieter: abrechnung.anbieter_gedruckt ?? auszug.bank ?? '',
    endziffern: abrechnung.endziffern ?? '',
    inhaber: auszug.inhaber ?? '',
    zeitraum_von: auszug.zeitraum_von ? datum(auszug.zeitraum_von) : '',
    zeitraum_bis: auszug.zeitraum_bis ? datum(auszug.zeitraum_bis) : '',
    // Gelesen, aber nicht lesbar: dann der gedruckte Text, damit man sieht, was dastand.
    anfangssaldo: auszug.anfangssaldo_cent !== null ? betragOhneZeichen(auszug.anfangssaldo_cent) : abrechnung.anfangssaldo_gedruckt ?? '',
    schlusssaldo: auszug.schlusssaldo_cent !== null ? betragOhneZeichen(auszug.schlusssaldo_cent) : abrechnung.schlusssaldo_gedruckt ?? '',
    faelliger_betrag: betragOhneZeichen(abrechnung.faelliger_betrag_cent),
    mindestbetrag: betragOhneZeichen(abrechnung.mindestbetrag_cent),
    ausgleich_betrag: betragOhneZeichen(abrechnung.ausgleich_betrag_cent),
  }))
  const anfangArt = saldoArtAus(auszug.anfangssaldo_cent, abrechnung.anfangssaldo_art)
  const schlussArt = saldoArtAus(auszug.schlusssaldo_cent, abrechnung.schlusssaldo_art)
  const [werte, setzeWerte] = useState<Werte>(anfang)
  const [artAnfang, setzeArtAnfang] = useState<SaldoArt | null>(anfangArt)
  const [artSchluss, setzeArtSchluss] = useState<SaldoArt | null>(schlussArt)
  const [fehler, setzeFehler] = useState<{ text: string; feld?: string } | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)

  const feld = (name: keyof Werte) => ({
    value: werte[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setzeWerte({ ...werte, [name]: name === 'endziffern' ? e.target.value.replace(/[^0-9]/g, '').slice(0, 4) : e.target.value }),
    'aria-invalid': fehler?.feld === name,
    autoComplete: 'off',
    'data-1p-ignore': true,
  })

  async function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    const koerper: Record<string, string | null> = {}
    for (const name of ['anbieter', 'endziffern', 'inhaber', 'zeitraum_von', 'zeitraum_bis', 'faelliger_betrag', 'mindestbetrag', 'ausgleich_betrag'] as const) {
      if (werte[name].trim() !== anfang[name].trim()) koerper[name] = werte[name].trim() || null
    }
    // Ein Saldo geht als Paar: Betrag und Bedeutung zusammen.
    if (werte.anfangssaldo.trim() !== anfang.anfangssaldo.trim() || artAnfang !== anfangArt) {
      koerper['anfangssaldo'] = werte.anfangssaldo.trim() || null
      if (artAnfang) koerper['anfangssaldo_art'] = artAnfang
    }
    if (werte.schlusssaldo.trim() !== anfang.schlusssaldo.trim() || artSchluss !== schlussArt) {
      koerper['schlusssaldo'] = werte.schlusssaldo.trim() || null
      if (artSchluss) koerper['schlusssaldo_art'] = artSchluss
    }
    if (Object.keys(koerper).length === 0) {
      aufSchliessen()
      return
    }
    setzeLaeuft(true)
    try {
      await api.aendern(`/api/karten/abrechnung/${auszug.id}/kopf`, koerper)
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler({
        text: problem instanceof ApiFehler ? problem.message : 'Die Änderung konnte nicht gespeichert werden.',
        ...(problem instanceof ApiFehler && problem.feld ? { feld: problem.feld } : {}),
      })
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Kartenkopf" offen={offen} aufSchliessen={aufSchliessen} breit>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler.text}</div>}
        <div className="feldreihe">
          <div><label htmlFor="kk-anbieter">Anbieter</label><input id="kk-anbieter" type="text" placeholder="American Express" {...feld('anbieter')} /></div>
          <div className="schmal"><label htmlFor="kk-ende">Letzte vier Ziffern</label><input id="kk-ende" type="text" inputMode="numeric" className="zahl" placeholder="1009" {...feld('endziffern')} /></div>
        </div>
        <div><label htmlFor="kk-inh">Karteninhaber</label><input id="kk-inh" type="text" {...feld('inhaber')} /></div>
        <div className="feldreihe">
          <div><label htmlFor="kk-von">Zeitraum von</label><input id="kk-von" type="text" className="zahl" placeholder="TT.MM.JJJJ" {...feld('zeitraum_von')} /></div>
          <div><label htmlFor="kk-bis">bis</label><input id="kk-bis" type="text" className="zahl" placeholder="TT.MM.JJJJ" {...feld('zeitraum_bis')} /></div>
        </div>
        <div className="feldreihe">
          <div>
            <label htmlFor="kk-anf">Alter Saldo</label>
            <input id="kk-anf" type="text" className="zahl" placeholder="1.234,56" {...feld('anfangssaldo')} />
            <SaldoArtWahl wert={artAnfang} setzeWert={setzeArtAnfang} beschriftung="Alter Saldo ist" />
          </div>
          <div>
            <label htmlFor="kk-schl">Neuer Saldo</label>
            <input id="kk-schl" type="text" className="zahl" placeholder="1.234,56" {...feld('schlusssaldo')} />
            <SaldoArtWahl wert={artSchluss} setzeWert={setzeArtSchluss} beschriftung="Neuer Saldo ist" />
          </div>
        </div>
        <div className="feldreihe">
          <div><label htmlFor="kk-faellig">Fälliger Betrag</label><input id="kk-faellig" type="text" className="zahl" placeholder="leer" {...feld('faelliger_betrag')} /></div>
          <div><label htmlFor="kk-mindest">Mindestbetrag</label><input id="kk-mindest" type="text" className="zahl" placeholder="leer" {...feld('mindestbetrag')} /></div>
          <div><label htmlFor="kk-einzug">Einzug laut Hinweis</label><input id="kk-einzug" type="text" className="zahl" placeholder="leer" {...feld('ausgleich_betrag')} /></div>
        </div>
        <div className="hinweis">
          Wird ein Guthaben nur auf das Girokonto zurücküberwiesen, ist das kein Einzug: das Feld dann leeren.
        </div>
        <div className="hinweis">
          Salden ohne Vorzeichen, dazu Schuld oder Guthaben wie auf der Abrechnung. Bei einer ersten Abrechnung ohne Vorsaldo 0,00 als alten Saldo eintragen.
          Ändern sich Anbieter oder Endziffern, wird die Karte neu erkannt.
        </div>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</button>
        </div>
      </form>
    </Fenster>
  )
}
